<template>
  <v-dialog
    :value="value"
    width="560"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
    @input="handleInput"
  >
    <v-card class="pa-6 background-pattern-icon">
      <div class="d-flex items-center mb-4">
        <span>
          <div class="icon-border">
            <Icon name="country_icon" />
          </div>
        </span>
        <v-spacer />
        <v-btn icon color="grey" @click="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      
      <div>
        <p class="text--secondary">
          You are about to add an Operating Country.
        </p>
      </div>
      
      <div class="mb-4">
        <CountryPicker
          :key="value"
          :modelValue="selectedCountry"
          pickerLabel="Country"
          label="Type to search"
          searchLabel="Search country"
          :items="availableCountries"
          :useStaticFallback="false"
          :useCode="false"
          :isSelectedItemRemove="true"
          @item-country-selected="onSelectCountry"
          :pickerPopUp="true"
        />
      </div>

      <div v-if="errorMessage" class="mb-4">
        <p class="text-caption error--text">{{ errorMessage }}</p>
      </div>
      <Button
        :loading="loading ? 'left' : null"
        label="Add"
        size="normal"
        :fullwidth="!selectedCountry ? 'disable' : 'hover'"
        :disabled="!selectedCountry"
        @onClick="handleAddCountry"
      />
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import CountryPicker from "@components/CountryPicker/CountryPicker.vue";
import Button from "@components/buttons/Button.vue";
import Icon from "@components/icons/Icon.vue";
import {
  OPERATING_ERROR_CODES,
  getOperatingErrorMessage,
} from "@components/constants/operatingErrorCodes";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  countries: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Backend error code to surface inline (e.g. err.response.data.code after a
   * 400 from the add-operating-country endpoint). Mapped to copy via the
   * shared operatingErrorCodes map; empty clears the message.
   */
  errorCode: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["onAdd", "input", "close"]);

const selectedCountry = ref(null);
const errorMessage = ref("");

/** Pass full list so ExSelectPicker can show already-added rows as disabled (gray). */
const availableCountries = computed(() => props.countries);

const onSelectCountry = (country) => {
  selectedCountry.value = country;
  errorMessage.value = "";
};

const handleAddCountry = () => {
  if (!selectedCountry.value) {
    return;
  }

  if (selectedCountry.value.disabled) {
    errorMessage.value = getOperatingErrorMessage(
      OPERATING_ERROR_CODES.COUNTRY_ALREADY_ADDED
    );
    return;
  }

  emit("onAdd", selectedCountry.value);
};

const handleClose = () => {
  emit("input", false);
  emit("close");
};

const handleInput = (value) => {
  emit("input", value);
};

// Reset state each time the dialog is opened.
watch(
  () => props.value,
  (isOpen) => {
    if (isOpen) {
      selectedCountry.value = null;
      errorMessage.value = "";
    }
  }
);

// Surface backend error codes (e.g. COUNTRY_ALREADY_ADDED) reported by the
// parent after an API failure.
watch(
  () => props.errorCode,
  (code) => {
    errorMessage.value = code ? getOperatingErrorMessage(code) : "";
  }
);
</script>

<style scoped>
.icon-border {
  width: 48px;
  height: 48px !important;
  padding: 12px !important;
  border-radius: 10px;
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280d;
}

.background-pattern-icon {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0px 0px;
}

::v-deep(.select-picker) {
  padding: 0px !important;
}
</style>

